<template>
    <div
        v-if="showing && bank"
        class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-50"
    >
        <div class="bg-white p-5 border-2 border-gray-500 rounded-lg w-11/12 max-w-3xl max-h-[90%] overflow-y-auto font-mono relative">
            <h2 class="text-xl font-semibold mb-4">Exchange at {{ bank.name }}</h2>
            <form @submit.prevent="onSubmit" class="space-y-4">
                <div>
                    <label class="block mb-1 font-medium">From</label>
                    <div class="relative flex space-x-2">
                        <select v-model="fromCurrency" class="border p-1 rounded">
                            <option v-for="c in uniqueCurrencies" :key="c" :value="c">{{ c }}</option>
                        </select>
                        <input
                            type="text"
                            v-model="fromInput"
                            @input="onFromInput($event)"
                            class="border p-1 rounded flex-1"
                        />
                        <button
                            type="button"
                            @click="setMaxAmount"
                            class="px-3 py-1 bg-gray-200 hover:bg-gray-300 border rounded text-sm"
                        >
                            Max
                        </button>
                        <p
                            v-if="isFormValid && !hasSufficientFunds"
                            class="absolute -bottom-6 right-0 text-sm text-red-600"
                        >
                            You only have
                            {{ props.money.find(m => m.code === fromCurrency)?.value || 0 }}
                            {{ fromCurrency }}, you can't exchange {{ fromAmount }}.
                        </p>
                    </div>
                </div>

                <div>
                    <label class="block mb-1 font-medium">To</label>
                    <div class="flex space-x-2">
                        <select v-model="toCurrency" class="border p-1 rounded">
                            <option v-for="c in uniqueCurrencies" :key="c" :value="c">{{ c }}</option>
                        </select>
                        <input
                            type="text"
                            v-model="toInput"
                            @input="onToInput($event)"
                            class="border p-1 rounded flex-1"
                        />
                    </div>
                </div>

                <!-- Rate display -->
                <p v-if="rate !== null" class="text-sm text-gray-600">
                    Rate: 1 {{ fromCurrency }} = {{ rate }} {{ toCurrency }}
                </p>
                <p v-else class="text-sm text-red-600">No rate available for this pair</p>

                <!-- Actions -->
                <div class="flex justify-end space-x-2">
                    <button type="button" @click="handleClose" class="px-4 py-2 border rounded">
                        Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="!canSubmit"
                        class="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                    >
                        Exchange
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { notify } from "@kyvg/vue3-notification";
import { api } from '@/utils/api';
import { useGameStore } from '@/stores/useGameStore.js'
import { roundMoney, moneyInCents } from '@/utils/gameplay.js';

const props = defineProps({
    bank:  { type: Object, required: true },
    money: { type: Array,  required: true }
});

const emit = defineEmits(['close','exchanged']);

const showing = defineModel('modelValue', {
    default: false,
    type: Boolean,
});

const gameStore = useGameStore();

const { gameTime } = storeToRefs(gameStore);

const currencyPairs = ref([]);
const exchanging = ref(false);
const fromCurrency   = ref('');
const toCurrency     = ref('');
const fromInput = ref('');
const toInput   = ref('');
const fromAmount = ref(0);
const toAmount   = ref(0);
const lastEdited     = ref('from');

// Extract a simple helper to load currencies for a given bank
async function loadCurrencies(bankId) {
    try {
        api({
            method: 'GET',
            endpoint: `api/banks/${bankId}/currencies`,
            params: {},
            successCallback: function(apiResponse) {
                currencyPairs.value = apiResponse.data.data || [];
                const all = uniqueCurrencies.value;
                fromCurrency.value = props.money[0]?.code || all[0] || '';
                toCurrency.value   = all.find(c => c !== fromCurrency.value) || all[1] || '';
            },
            errorCallback: function() {
                notify({
                    title: "Error",
                    text: 'Failed to load currencies',
                    group: "danger",
                    duration: 5000,
                });
            }
        });
    } catch (err) {
        console.error('Failed to load currencies', err);
    }
}

// Watch `bank` prop—when it becomes defined (and has an `id`), fetch
watch(
    () => props.bank && props.bank.id,
    (bankId) => {
        if (bankId) {
            loadCurrencies(bankId).then(() => {
                fromInput.value = '0.00';
                toInput.value   = '0.00';
                fromAmount.value = 0;
                toAmount.value   = 0;
            });
        }
    },
    { immediate: true }  // so if bank is already set before mount, we still fetch
);

// --- Helpers ---
const uniqueCurrencies = computed(() => {
    const set = new Set();
    currencyPairs.value.forEach(p => {
        set.add(p.from_currency);
        set.add(p.to_currency);
    });
    return Array.from(set);
});

const rate = computed(() => {
    const pair = currencyPairs.value.find(
        p => p.from_currency === fromCurrency.value && p.to_currency === toCurrency.value
    );
    return pair ? pair.rate : null;
});

// Re-compute the target whenever the source changes
watch([fromAmount, rate], ([f, r]) => {
    if (lastEdited.value === 'from' && r != null) {
        toAmount.value = parseFloat((f * r).toFixed(2));
    }
});

// And vice versa
watch([toAmount, rate], ([t, r]) => {
    if (lastEdited.value === 'to' && r != null) {
        fromAmount.value = parseFloat((t / r).toFixed(2));
    }
});

// Reset amounts if you switch currencies
watch([fromCurrency, toCurrency], () => {
    fromAmount.value = 0;
    toAmount.value   = 0;
});

// Track which field was edited last
function onFromInput(evt) {
    lastEdited.value = 'from';
    const clean = sanitize(evt.target.value, 2);
    fromInput.value  = clean;
    fromAmount.value = parseFloat(clean) || 0;

    if (rate.value != null) {
        const converted = (fromAmount.value * rate.value).toFixed(2);
        toInput.value  = converted;
        toAmount.value = parseFloat(converted) || 0;
    }
}

function onToInput(evt) {
    lastEdited.value = 'to';
    const clean = sanitize(evt.target.value, 2);
    toInput.value   = clean;
    toAmount.value  = parseFloat(clean) || 0;

    if (rate.value != null) {
        const converted = (toAmount.value / rate.value).toFixed(2);
        fromInput.value  = converted;
        fromAmount.value = parseFloat(converted) || 0;
    }
}

function setMaxAmount() {
    const walletEntry = props.money.find(m => m.code === fromCurrency.value);
    const maxAmount = roundMoney(walletEntry?.value || 0);
    
    if (maxAmount > 0) {
        lastEdited.value = 'from';
        fromInput.value = maxAmount.toFixed(2);
        fromAmount.value = maxAmount;

        // Calculate the converted amount if rate is available
        if (rate.value != null) {
            const converted = (maxAmount * rate.value).toFixed(2);
            toInput.value = converted;
            toAmount.value = parseFloat(converted) || 0;
        }
    }
}

const isFormValid = computed(() => {
    console.log(rate.value);
    return (
        rate.value !== null &&
        fromAmount.value > 0 &&
        toAmount.value > 0
    );
});

const hasSufficientFunds = computed(() => {
    const walletEntry = props.money.find(m => m.code === fromCurrency.value);
    return walletEntry != null && moneyInCents(walletEntry.value) >= moneyInCents(fromAmount.value);
});

const canSubmit = computed(() => !exchanging.value && isFormValid.value && hasSufficientFunds.value);

async function onSubmit() {
    if (!canSubmit.value) return;
    exchanging.value = true;
    try {
        const params = {
            from_currency: fromCurrency.value,
            amount:   fromAmount.value,
            to_currency:   toCurrency.value,
            time: gameTime.value
        };
        api({
            method: 'POST',
            endpoint: `api/banks/${props.bank.id}/exchange`,
            params: params,
            successCallback: function(apiResponse) {
                exchanging.value = false;
                emit('exchanged', apiResponse.data.data);
                emit('close');
            },
            errorCallback: function() {
                exchanging.value = false;
                notify({
                    title: "Error",
                    text: 'Failed to exchange money',
                    group: "danger",
                    duration: 5000,
                });
            }
        });
    } catch (err) {
        exchanging.value = false;
        console.error('Exchange failed', err);
        // optionally show a notification...
    } finally {
        // Reset inputs
        fromInput.value = '0.00';
        toInput.value   = '0.00';
        fromAmount.value = 0;
        toAmount.value   = 0;
    }
}

function handleClose() {
    emit('close');
    // Reset inputs
    fromInput.value = '0.00';
    toInput.value   = '0.00';
    fromAmount.value = 0;
    toAmount.value   = 0;
}

function sanitize(value, maxDecimals = 2) {
    // 1) strip out anything but digits and dots
    const cleaned = value.replace(/[^\d.]/g, '');
    // 2) match from start: any digits, optionally one dot + up to maxDecimals digits
    const regex = new RegExp(`^\\d*(?:\\.\\d{0,${maxDecimals}})?`);
    const match = cleaned.match(regex);
    let result = match ? match[0] : '';
    // 3) if user starts with ".", prefix a zero
    if (result === '.') {
        return '0.';
    } else if (result.startsWith('.') ) {
        return '0' + result;
    }
    return result;
}
</script>
